import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import {
  AlertTriangle,
  ArrowLeft,
  ChevronDown,
  Loader2,
  Plus,
  RefreshCw,
  Trash2,
} from "lucide-react";

type Source = { label: string; ref: string };

interface CanonEntry {
  id: string;
  canon_id: string;
  section: string;
  display_order: number;
  statement: string;
  boundaries: string | null;
  sources: Source[];
  status: string;
  version: number;
  pending_statement: string | null;
  pending_boundaries: string | null;
  pending_sources: Source[] | null;
  pending_change_note: string | null;
  created_at: string;
  updated_at: string;
}

interface ArtifactLink {
  id: string;
  canon_entry_id: string;
  is_stale: boolean;
}

interface CanonCommit {
  id: string;
  section: string;
  committed_at: string;
  entries_changed: number;
  amendments_adopted: number;
  links_staled: number;
  note: string | null;
}

type StagedEdit = {
  kind: "edit";
  statement: string;
  boundaries: string;
  sources: Source[];
  display_order: number;
};

type StagedAmendment = {
  kind: "amendment";
  statement: string;
  change_note: string;
};

type Staged = StagedEdit | StagedAmendment;

function toSources(value: unknown): Source[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((s) => s && typeof s === "object")
    .map((s) => ({
      label: String((s as any).label ?? ""),
      ref: String((s as any).ref ?? ""),
    }));
}

export default function CanonSectionWorkspace() {
  const { section: sectionParam } = useParams();
  const section = decodeURIComponent(sectionParam || "");
  const { toast } = useToast();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState<CanonEntry[]>([]);
  const [links, setLinks] = useState<ArtifactLink[]>([]);
  const [commits, setCommits] = useState<CanonCommit[]>([]);
  const [saving, setSaving] = useState(false);

  const [staged, setStaged] = useState<Record<string, Staged>>({});

  // staging editors
  const [editTarget, setEditTarget] = useState<CanonEntry | null>(null);
  const [editDraft, setEditDraft] = useState<StagedEdit>({
    kind: "edit",
    statement: "",
    boundaries: "",
    sources: [],
    display_order: 0,
  });

  const [amendTarget, setAmendTarget] = useState<CanonEntry | null>(null);
  const [amendDraft, setAmendDraft] = useState({ statement: "", change_note: "" });

  const [ratifyTarget, setRatifyTarget] = useState<CanonEntry | null>(null);
  const [sealTarget, setSealTarget] = useState<CanonEntry | null>(null);
  const [discardTarget, setDiscardTarget] = useState<CanonEntry | null>(null);

  const [reviewOpen, setReviewOpen] = useState(false);
  const [commitNote, setCommitNote] = useState("");
  const [commitSummary, setCommitSummary] = useState<string | null>(null);
  const [commitFailures, setCommitFailures] = useState<string[]>([]);

  async function load() {
    setLoading(true);
    try {
      const [entryRes, linkRes, commitRes] = await Promise.all([
        supabase
          .from("canon_entries")
          .select("*")
          .eq("section", section)
          .order("display_order", { ascending: true }),
        supabase.from("artifact_links").select("id, canon_entry_id, is_stale"),
        supabase
          .from("canon_commits")
          .select("*")
          .eq("section", section)
          .order("committed_at", { ascending: false }),
      ]);
      if (entryRes.error) throw entryRes.error;
      if (linkRes.error) throw linkRes.error;
      if (commitRes.error) throw commitRes.error;
      setEntries(
        (entryRes.data || []).map((e: any) => ({
          ...e,
          sources: toSources(e.sources),
          pending_sources: e.pending_sources ? toSources(e.pending_sources) : null,
        })) as CanonEntry[],
      );
      setLinks((linkRes.data || []) as ArtifactLink[]);
      setCommits((commitRes.data || []) as CanonCommit[]);
    } catch (err: any) {
      toast({
        title: "Could not load this section",
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    setStaged({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  const dependents = (entryId: string) =>
    links.filter((l) => l.canon_entry_id === entryId);

  const stagedList = useMemo(
    () =>
      entries
        .filter((e) => staged[e.id])
        .map((e) => ({ entry: e, change: staged[e.id] })),
    [entries, staged],
  );

  const stagedCount = stagedList.length;

  function openEdit(entry: CanonEntry) {
    const existing = staged[entry.id];
    const base: StagedEdit =
      existing && existing.kind === "edit"
        ? existing
        : {
            kind: "edit",
            statement: entry.statement,
            boundaries: entry.boundaries || "",
            sources: entry.sources,
            display_order: entry.display_order,
          };
    setEditDraft(base);
    setEditTarget(entry);
  }

  function stageEdit() {
    if (!editTarget) return;
    if (!editDraft.statement.trim()) {
      toast({
        title: "Statement required",
        description: "An entry needs a statement.",
        variant: "destructive",
      });
      return;
    }
    setStaged((prev) => ({ ...prev, [editTarget.id]: { ...editDraft } }));
    setEditTarget(null);
  }

  function openAmend(entry: CanonEntry) {
    const existing = staged[entry.id];
    if (existing && existing.kind === "amendment") {
      setAmendDraft({ statement: existing.statement, change_note: existing.change_note });
    } else if (entry.pending_statement) {
      setAmendDraft({
        statement: entry.pending_statement,
        change_note: entry.pending_change_note ?? "",
      });
    } else {
      setAmendDraft({ statement: entry.statement, change_note: "" });
    }
    setAmendTarget(entry);
  }

  function stageAmendment() {
    if (!amendTarget) return;
    if (!amendDraft.statement.trim() || !amendDraft.change_note.trim()) {
      toast({
        title: "Missing fields",
        description: "A new statement and a change note are both required.",
        variant: "destructive",
      });
      return;
    }
    setStaged((prev) => ({
      ...prev,
      [amendTarget.id]: {
        kind: "amendment",
        statement: amendDraft.statement.trim(),
        change_note: amendDraft.change_note.trim(),
      },
    }));
    setAmendTarget(null);
  }

  function unstage(entryId: string) {
    setStaged((prev) => {
      const next = { ...prev };
      delete next[entryId];
      return next;
    });
  }

  async function ratify(entry: CanonEntry) {
    setSaving(true);
    try {
      const { error } = await supabase
        .from("canon_entries")
        .update({
          status: "ratified",
          ratified_by: user?.id ?? null,
          ratified_at: new Date().toISOString(),
        })
        .eq("id", entry.id);
      if (error) throw error;
      toast({ title: `${entry.canon_id} ratified` });
      setRatifyTarget(null);
      await load();
    } catch (err: any) {
      toast({ title: "Ratify failed", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  async function sealAmendment(entry: CanonEntry) {
    if (!entry.pending_statement || !entry.pending_change_note) return;
    setSaving(true);
    try {
      const affected = dependents(entry.id).length;
      const { error } = await supabase
        .from("canon_entries")
        .update({
          statement: entry.pending_statement,
          boundaries: entry.pending_boundaries ?? entry.boundaries,
          sources: (entry.pending_sources ?? entry.sources) as any,
          change_note: entry.pending_change_note,
          ratified_by: user?.id ?? null,
          ratified_at: new Date().toISOString(),
          pending_statement: null,
          pending_boundaries: null,
          pending_sources: null,
          pending_change_note: null,
          pending_staged_by: null,
          pending_staged_at: null,
        })
        .eq("id", entry.id);
      if (error) throw error;
      toast({
        title: `${entry.canon_id} sealed`,
        description: `New statement is law. ${affected} dependent link${
          affected === 1 ? "" : "s"
        } marked stale.`,
      });
      setSealTarget(null);
      await load();
    } catch (err: any) {
      toast({ title: "Seal failed", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  async function discardPending(entry: CanonEntry) {
    setSaving(true);
    try {
      const { error } = await supabase
        .from("canon_entries")
        .update({
          pending_statement: null,
          pending_boundaries: null,
          pending_sources: null,
          pending_change_note: null,
          pending_staged_by: null,
          pending_staged_at: null,
        })
        .eq("id", entry.id);
      if (error) throw error;
      toast({ title: "Pending amendment discarded" });
      setDiscardTarget(null);
      await load();
    } catch (err: any) {
      toast({ title: "Discard failed", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  function openReview() {
    setCommitNote("");
    setCommitSummary(null);
    setCommitFailures([]);
    setReviewOpen(true);
  }

  async function commitSection() {
    setSaving(true);
    const edits = stagedList.filter((s) => s.change.kind === "edit");
    const amendments = stagedList.filter((s) => s.change.kind === "amendment");
    const ordered = [...edits, ...amendments];

    let entriesChanged = 0;
    let amendmentsStaged = 0;
    const succeeded: string[] = [];
    const failures: string[] = [];
    const remaining = { ...staged };

    try {
      for (const item of ordered) {
        const change = item.change;
        const payload =
          change.kind === "edit"
            ? {
                statement: change.statement.trim(),
                boundaries: change.boundaries.trim() || null,
                sources: change.sources.filter((s) => s.label.trim() || s.ref.trim()) as any,
                display_order: Number(change.display_order) || 0,
              }
            : {
                pending_statement: change.statement,
                pending_change_note: change.change_note,
                pending_staged_by: user?.id ?? null,
                pending_staged_at: new Date().toISOString(),
              };

        const { error } = await supabase
          .from("canon_entries")
          .update(payload)
          .eq("id", item.entry.id);

        if (error) {
          failures.push(`${item.entry.canon_id}: ${error.message}`);
          break;
        }

        delete remaining[item.entry.id];
        succeeded.push(item.entry.canon_id);
        entriesChanged += 1;
        if (change.kind === "amendment") {
          amendmentsStaged += 1;
        }
      }

      setStaged(remaining);

      if (entriesChanged > 0) {
        const { error: commitError } = await supabase.from("canon_commits").insert({
          section,
          committed_by: user?.id ?? null,
          entries_changed: entriesChanged,
          amendments_adopted: amendmentsStaged,
          links_staled: 0,
          note: commitNote.trim() || null,
        });
        if (commitError) {
          failures.push(`Commit log: ${commitError.message}`);
        }
      }

      setCommitFailures(failures);
      setCommitSummary(
        `${entriesChanged} entr${entriesChanged === 1 ? "y" : "ies"} changed, ` +
          `${amendmentsStaged} amendment${amendmentsStaged === 1 ? "" : "s"} staged for ratification.` +
          (succeeded.length ? ` Written: ${succeeded.join(", ")}.` : ""),
      );
      await load();
    } catch (err: any) {
      setCommitFailures([err.message]);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="p-4 md:p-6 space-y-6 pb-32">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="space-y-1">
          <Link
            to="/admin/canon"
            className="text-sm text-muted-foreground inline-flex items-center gap-1 hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to register
          </Link>
          <h1 className="text-2xl font-bold">{section}</h1>
          <p className="text-sm text-muted-foreground">
            Section workspace. Edits stage here and write on commit.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading section...
        </div>
      ) : entries.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            This section has no entries.
          </CardContent>
        </Card>
      ) : (
        entries.map((entry) => {
          const change = staged[entry.id];
          const deps = dependents(entry.id);
          const staleCount = deps.filter((d) => d.is_stale).length;
          const shownStatement =
            change?.kind === "edit" ? change.statement : entry.statement;
          const pendingStatement =
            change?.kind === "amendment" ? change.statement : entry.pending_statement;
          const pendingNote =
            change?.kind === "amendment" ? change.change_note : entry.pending_change_note;
          const hasPending = !!pendingStatement;
          return (
            <Card
              key={entry.id}
              className={change ? "border-primary/60 ring-1 ring-primary/20" : undefined}
            >
              <CardHeader className="pb-3">
                <div className="flex flex-wrap items-center gap-2">
                  <CardTitle className="text-base font-mono">{entry.canon_id}</CardTitle>
                  <Badge variant={entry.status === "ratified" ? "default" : "secondary"}>
                    {entry.status}
                  </Badge>
                  <Badge variant="outline">v{entry.version}</Badge>
                  <Badge variant="outline">
                    {deps.length} dependent{deps.length === 1 ? "" : "s"}
                  </Badge>
                  {staleCount > 0 && (
                    <Badge variant="destructive" className="gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      {staleCount} stale
                    </Badge>
                  )}
                  {entry.pending_statement && (
                    <Badge className="gap-1 bg-amber-500/15 text-amber-700 dark:text-amber-400 hover:bg-amber-500/20">
                      <AlertTriangle className="h-3 w-3" />
                      amendment pending
                    </Badge>
                  )}
                  {change && <Badge className="bg-primary/15 text-primary">staged</Badge>}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-lg leading-relaxed">{shownStatement}</p>
                {hasPending && (
                  <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 space-y-1">
                    <p className="text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
                      Pending{change?.kind === "amendment" ? " (staged)" : ""}
                    </p>
                    <p className="whitespace-pre-wrap">{pendingStatement}</p>
                    {pendingNote && (
                      <p className="text-xs text-muted-foreground">Change note: {pendingNote}</p>
                    )}
                  </div>
                )}


                <Collapsible>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="sm" className="gap-1 px-0">
                      <ChevronDown className="h-4 w-4" />
                      Boundaries and sources
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-2 space-y-3 text-sm">
                    <div>
                      <p className="font-medium">Boundaries</p>
                      <p className="text-muted-foreground whitespace-pre-wrap">
                        {(change?.kind === "edit" ? change.boundaries : entry.boundaries) ||
                          "None recorded."}
                      </p>
                    </div>
                    <div>
                      <p className="font-medium">Sources</p>
                      {(change?.kind === "edit" ? change.sources : entry.sources).length === 0 ? (
                        <p className="text-muted-foreground">None recorded.</p>
                      ) : (
                        <ul className="list-disc pl-5 text-muted-foreground">
                          {(change?.kind === "edit" ? change.sources : entry.sources).map(
                            (s, i) => (
                              <li key={i}>
                                {s.label}
                                {s.ref ? `: ${s.ref}` : ""}
                              </li>
                            ),
                          )}
                        </ul>
                      )}
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                <Separator />

                <div className="flex flex-wrap gap-2">
                  {entry.status === "proposed" ? (
                    <>
                      <Button variant="outline" size="sm" onClick={() => openEdit(entry)}>
                        {change ? "Edit staged change" : "Edit"}
                      </Button>
                      <Button size="sm" onClick={() => setRatifyTarget(entry)}>
                        Ratify
                      </Button>
                    </>
                  ) : (
                    <>
                      {entry.pending_statement && !change && (
                        <Button size="sm" onClick={() => setSealTarget(entry)}>
                          Ratify amendment v{entry.version + 1}
                        </Button>
                      )}
                      <Button variant="outline" size="sm" onClick={() => openAmend(entry)}>
                        {change
                          ? "Edit staged amendment"
                          : entry.pending_statement
                            ? "Edit pending amendment"
                            : "Propose Amendment"}
                      </Button>
                      {entry.pending_statement && !change && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDiscardTarget(entry)}
                        >
                          Discard pending
                        </Button>
                      )}
                    </>
                  )}
                  {change && (
                    <Button variant="ghost" size="sm" onClick={() => unstage(entry.id)}>
                      Discard change
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })
      )}

      {/* Commit history */}
      <Collapsible>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-1 px-0">
            <ChevronDown className="h-4 w-4" />
            Commit history ({commits.length})
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-2 space-y-2">
          {commits.length === 0 ? (
            <p className="text-sm text-muted-foreground">No commits recorded for this section.</p>
          ) : (
            commits.map((c) => (
              <div key={c.id} className="rounded-md border p-3 text-sm space-y-1">
                <p className="text-xs text-muted-foreground">
                  {new Date(c.committed_at).toLocaleString()}
                </p>
                <p>
                  {c.entries_changed} changed · {c.amendments_adopted} amendments ·{" "}
                  {c.links_staled} links stale
                </p>
                {c.note && <p className="text-muted-foreground">{c.note}</p>}
              </div>
            ))
          )}
        </CollapsibleContent>
      </Collapsible>

      {/* Staged bar */}
      {stagedCount > 0 && (
        <div className="fixed bottom-0 left-0 right-0 border-t bg-background/95 backdrop-blur px-4 py-3 z-40">
          <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3">
            <p className="text-sm font-medium">
              {stagedCount} staged change{stagedCount === 1 ? "" : "s"} in this section
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setStaged({})}>
                Discard all
              </Button>
              <Button size="sm" onClick={openReview}>
                Commit section
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Stage edit dialog */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Stage edit to {editTarget?.canon_id}</DialogTitle>
            <DialogDescription>
              Nothing is written until you commit the section.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label>Display order</Label>
              <Input
                type="number"
                value={editDraft.display_order}
                onChange={(e) =>
                  setEditDraft({ ...editDraft, display_order: Number(e.target.value) || 0 })
                }
              />
            </div>
            <div className="space-y-1">
              <Label>Statement</Label>
              <Textarea
                rows={4}
                value={editDraft.statement}
                onChange={(e) => setEditDraft({ ...editDraft, statement: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label>Boundaries</Label>
              <Textarea
                rows={3}
                value={editDraft.boundaries}
                onChange={(e) => setEditDraft({ ...editDraft, boundaries: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Sources</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setEditDraft({
                      ...editDraft,
                      sources: [...editDraft.sources, { label: "", ref: "" }],
                    })
                  }
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add source
                </Button>
              </div>
              {editDraft.sources.map((s, i) => (
                <div key={i} className="flex gap-2">
                  <Input
                    placeholder="Label"
                    value={s.label}
                    onChange={(e) => {
                      const next = [...editDraft.sources];
                      next[i] = { ...next[i], label: e.target.value };
                      setEditDraft({ ...editDraft, sources: next });
                    }}
                  />
                  <Input
                    placeholder="Ref"
                    value={s.ref}
                    onChange={(e) => {
                      const next = [...editDraft.sources];
                      next[i] = { ...next[i], ref: e.target.value };
                      setEditDraft({ ...editDraft, sources: next });
                    }}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() =>
                      setEditDraft({
                        ...editDraft,
                        sources: editDraft.sources.filter((_, j) => j !== i),
                      })
                    }
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)}>
              Cancel
            </Button>
            <Button onClick={stageEdit}>Stage change</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stage amendment dialog */}
      <Dialog open={!!amendTarget} onOpenChange={(o) => !o && setAmendTarget(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Draft amendment to {amendTarget?.canon_id}</DialogTitle>
            <DialogDescription>
              The amendment stages here. Committing the section records it as pending: it becomes
              law only when you press Ratify on the entry.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Current statement (v{amendTarget?.version})</Label>
                <Textarea rows={6} readOnly value={amendTarget?.statement || ""} />
              </div>
              <div className="space-y-1">
                <Label>Pending statement</Label>
                <Textarea
                  rows={6}
                  value={amendDraft.statement}
                  onChange={(e) => setAmendDraft({ ...amendDraft, statement: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Change note (required)</Label>
              <Textarea
                rows={3}
                value={amendDraft.change_note}
                onChange={(e) => setAmendDraft({ ...amendDraft, change_note: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAmendTarget(null)}>
              Cancel
            </Button>
            <Button onClick={stageAmendment}>Stage amendment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ratify confirm */}
      <AlertDialog open={!!ratifyTarget} onOpenChange={(o) => !o && setRatifyTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ratify {ratifyTarget?.canon_id}?</AlertDialogTitle>
            <AlertDialogDescription>
              Ratification writes immediately and is not staged. After it, this entry changes only
              through an amendment with a change note.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (ratifyTarget) ratify(ratifyTarget);
              }}
            >
              Ratify
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Seal confirm */}
      <AlertDialog open={!!sealTarget} onOpenChange={(o) => !o && setSealTarget(null)}>
        <AlertDialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Ratify {sealTarget?.canon_id}?</AlertDialogTitle>
            <AlertDialogDescription>
              Sealing makes the new statement law: the current version archives, the version number
              rises, and dependent links go stale.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="rounded-md border p-3">
                <p className="font-medium">Current (v{sealTarget?.version})</p>
                <p className="text-muted-foreground whitespace-pre-wrap">
                  {sealTarget?.statement}
                </p>
              </div>
              <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3">
                <p className="font-medium">Pending</p>
                <p className="whitespace-pre-wrap">{sealTarget?.pending_statement}</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Change note: {sealTarget?.pending_change_note}
            </p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (sealTarget) sealAmendment(sealTarget);
              }}
            >
              Ratify
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Discard pending confirm */}
      <AlertDialog open={!!discardTarget} onOpenChange={(o) => !o && setDiscardTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Discard the pending amendment to {discardTarget?.canon_id}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              The pending wording and its change note are cleared. The current statement stays law
              and nothing is archived.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (discardTarget) discardPending(discardTarget);
              }}
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Commit review */}
      <Dialog open={reviewOpen} onOpenChange={(o) => !o && setReviewOpen(false)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Commit {section}</DialogTitle>
            <DialogDescription>
              Review every staged change before it is written.
            </DialogDescription>
          </DialogHeader>

          {commitSummary ? (
            <div className="space-y-3">
              <div className="rounded-md border border-primary/40 bg-primary/5 p-4 text-sm">
                {commitSummary}
              </div>
              {commitFailures.length > 0 && (
                <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm space-y-1">
                  <p className="font-medium">Failed, still staged:</p>
                  {commitFailures.map((f, i) => (
                    <p key={i}>{f}</p>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {stagedList.map(({ entry, change }) => (
                <div key={entry.id} className="rounded-md border p-3 space-y-2 text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono">{entry.canon_id}</span>
                    <Badge variant="outline">
                      {change.kind === "amendment" ? "amendment (staged for ratification)" : "entry update"}
                    </Badge>
                  </div>
                  {change.kind === "amendment" ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <p className="font-medium">Current (stays law)</p>
                        <p className="text-muted-foreground whitespace-pre-wrap">
                          {entry.statement}
                        </p>
                      </div>
                      <div>
                        <p className="font-medium">Pending</p>
                        <p className="whitespace-pre-wrap">{change.statement}</p>
                      </div>
                    </div>
                  ) : (
                    <p className="whitespace-pre-wrap">{change.statement}</p>
                  )}
                  {change.kind === "amendment" && (
                    <p className="text-xs text-muted-foreground">
                      Change note: {change.change_note}
                    </p>
                  )}
                </div>
              ))}
              <div className="space-y-1">
                <Label>Commit note (optional)</Label>
                <Textarea
                  rows={2}
                  value={commitNote}
                  onChange={(e) => setCommitNote(e.target.value)}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewOpen(false)}>
              {commitSummary ? "Close" : "Cancel"}
            </Button>
            {!commitSummary && (
              <Button onClick={commitSection} disabled={saving || stagedCount === 0}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Commit {stagedCount} change{stagedCount === 1 ? "" : "s"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
